import axios, { AxiosError } from 'axios'

// The public landing page talks to the unauthenticated `/api/*` surface,
// NOT the dashboard's `/dashboard/api` client. Requests are rate-limited
// per-IP by the backend (5/min for arbitrary demo calls).
const http = axios.create({
  baseURL: '/',
  headers: { 'Content-Type': 'application/json' },
})

// ---- Response shapes -------------------------------------------------------

export interface HeroPair {
  nameA: string
  nameB: string
  isMatch: boolean
  sport: string
  method: string
  tag: string
}

export interface ExamplePlaceholder {
  reference: string
  names: string[]
}

export interface ExamplesResponse {
  placeholders: Record<string, ExamplePlaceholder>
  hero_pairs: HeroPair[]
}

export interface LiveStats {
  requests_60s: number
  avg_response_ms: number
  avg_processing_ms: number
  events_processed_60s: number
  pairs_compared_60s: number
  pairs_processed_60s: number
  uptime_seconds: number
  maintenance: boolean
}

export interface MatchDetail {
  name: string
  match: boolean
  confidence: number
  ambiguous: boolean
  method: string
  reason_code: string
  normalized_name: string
  qualifiers?: Record<string, unknown> | null
  cached?: boolean
  ambiguous_candidates?: string[]
}

export interface MatchResponse {
  reference: string
  any_match: boolean
  details: MatchDetail[]
  processing_time_ms: number
}

export interface MatchRequest {
  reference: string
  names: string[]
  sport: string
}

// ---- Calls -----------------------------------------------------------------

export async function fetchExamples(): Promise<ExamplesResponse> {
  const { data } = await http.get<ExamplesResponse>('/api/examples')
  return data
}

export async function fetchStats(): Promise<LiveStats> {
  const { data } = await http.get<LiveStats>('/api')
  return data
}

export async function postMatch(body: MatchRequest): Promise<MatchResponse> {
  const { data } = await http.post<MatchResponse>('/api/match', body)
  return data
}

// ---- Error helpers ---------------------------------------------------------

export type DemoErrorKind = 'rate_limited' | 'network' | 'server'

export interface DemoError {
  kind: DemoErrorKind
  retryAfterSeconds?: number
}

/**
 * Normalise an unknown error thrown by a demo `/api/match` call into a small
 * tagged union the UI can branch on. 401 is treated the same as 429 during the
 * open-demo transition (the gate may not be live yet on the server).
 */
export function classifyDemoError(err: unknown): DemoError {
  const axErr = err as AxiosError
  if (axErr?.isAxiosError) {
    const status = axErr.response?.status
    if (status === 429 || status === 401) {
      const header = axErr.response?.headers?.['retry-after']
      const retryAfterSeconds =
        typeof header === 'string' && /^\d+$/.test(header) ? Number(header) : undefined
      return { kind: 'rate_limited', retryAfterSeconds }
    }
    if (status === undefined) {
      return { kind: 'network' }
    }
  }
  return { kind: 'server' }
}
